import { Button } from '@/atoms/Button';
import { CalendarButton } from '@/atoms/CalendarButton';
import { useAdaptiveWidth } from '@/hooks/useAdaptiveWidth';
import { useEnhancedRouter } from '@/hooks/useEnhancedRouter';
import { useShowMore } from '@/hooks/useShowMore';
import { fetchData } from '@/services/fetchData';
import { BackendBookingDateType, EventType, Palettes, Variants } from '@/types';
import { getLocaleDate } from '@/utils/getLocaleDate';
import { parseText } from '@/utils/parseText';
import cn from 'classnames';
import { useEffect, useState } from 'react';
import { Calendar, CalendarType } from '../Calendar';
import { EventCard } from '../EventCard';
import styles from './SubEventsBlock.module.scss';

type SubEventsBlockProps = {
  events: EventType[];
  className?: string;
  code: string;
  translations: Record<string, string>;
  closed?: boolean;
  dates: BackendBookingDateType[];
  archive?: boolean;
};

export function SubEventsBlock({
  events,
  code,
  className,
  translations,
  archive,
  dates,
  closed,
}: SubEventsBlockProps) {
  const [isClient, setIsClient] = useState(false);
  const [eventList, setEventList] = useState(events);
  const [calendarActive, setCalendarActive] = useState(false);
  const [date, setDate] = useState<Date | undefined>(undefined);
  const parsedDates = Array.from(
    new Set(dates.map((item) => new Date(item.date).toISOString())),
  )
    .map((date) => new Date(date))
    .sort((a, b) => a.getTime() - b.getTime());

  const calendarDates = dates.map((item) => ({
    date: new Date(item.date),
  }));

  useEffect(() => setIsClient(true), []);

  const { isTablet } = useAdaptiveWidth();
  const numberOfShown = isTablet ? 4 : 8;
  const { getMore, items, allShown } = useShowMore(
    eventList,
    numberOfShown,
    `subevents_${code}`,
  );

  const wrapperClassName = cn(styles.wrapper, className);

  const handleDateChange = async (date: Date) => {
    setDate(date);
    const response = await fetchData({
      url: `/content/event/${code}/subEvents?date=${date.toLocaleDateString(
        'ru-RU',
      )}${archive ? '&archive=1' : ''}`,
    });

    if (response?.list) {
      setEventList(response.list);
    }

    setCalendarActive(false);
  };

  const getButtonLabel = (event: EventType) => {
    if (closed) return translations.closed;
    return event.button;
  };

  const getEventClosed = (event: EventType) => {
    return (
      !event.needRegistration || event.closed || closed || dates.length === 0
    );
  };

  return (
    <div className={wrapperClassName}>
      <div className={styles.heading}>
        <h4 className={styles.title}>
          {parseText(translations.subEventTitle)}
        </h4>
        <DatesListDesktop
          parsedDates={parsedDates}
          date={date}
          handleDateChange={handleDateChange}
          setCalendarOpened={setCalendarActive}
        />
        <DatesListMobile
          parsedDates={parsedDates}
          date={date}
          handleDateChange={handleDateChange}
          setCalendarOpened={setCalendarActive}
        />
      </div>
      {events && events.length > 0 && (
        <>
          <div className={styles.cards}>
            {items.length === 0 ? (
              <p className={styles.empty}>{translations.noEvents}</p>
            ) : (
              items.map((event, index) => (
                <EventCard
                  key={event.code + index}
                  tag={event.tag}
                  title={event.name}
                  description={event.text}
                  button={getButtonLabel(event)}
                  dateFrom={event.dateFrom}
                  dateTo={event.dateTo}
                  dateText={event.dateText}
                  code={event.code}
                  info={`${event.timeFrom}${
                    event.timeTo ? '-' + event.timeTo : ''
                  }`}
                  parentEvent={code}
                  closed={getEventClosed(event)}
                  tagLink={event.tagLink}
                />
              ))
            )}
          </div>
          {!allShown && events.length > numberOfShown && (
            <Button
              text={translations.showMore}
              onClick={getMore}
              variant={Variants.Outline}
              palette={Palettes.Secondary}
              className={styles.more}
            />
          )}
        </>
      )}
      {isClient && calendarActive && (
        <Calendar
          type={CalendarType.Modal}
          date={date || new Date()}
          onChange={handleDateChange}
          closeFn={() => setCalendarActive(false)}
          noAutoChange
          availableDates={calendarDates}
        />
      )}
    </div>
  );
}

const DatesListDesktop = ({
  parsedDates,
  date,
  handleDateChange,
  setCalendarOpened,
}: {
  parsedDates: Date[];
  date: Date | undefined;
  handleDateChange: (date: Date) => void;
  setCalendarOpened: (opened: boolean) => void;
}) => {
  const { locale } = useEnhancedRouter();

  return (
    <div className={styles.dates}>
      <div className={styles.datesList}>
        {parsedDates
          .map((dateEl, index) => (
            <Button
              key={index}
              variant={Variants.Outline}
              onClick={() => handleDateChange(dateEl)}
              text={getLocaleDate(dateEl, locale)}
              palette={Palettes.Secondary}
              className={date === dateEl ? styles.active : styles.date}
            />
          ))
          .slice(0, 3)}
      </div>
      <CalendarButton
        onClick={() => setCalendarOpened(true)}
        label="Расписание"
      />
    </div>
  );
};

const DatesListMobile = ({
  parsedDates,
  date,
  handleDateChange,
  setCalendarOpened,
}: {
  parsedDates: Date[];
  date: Date | undefined;
  handleDateChange: (date: Date) => void;
  setCalendarOpened: (opened: boolean) => void;
}) => {
  const { locale } = useEnhancedRouter();

  return (
    <div className={styles.datesMobile}>
      <div className={styles.calendarButton}>
        <CalendarButton
          onClick={() => setCalendarOpened(true)}
          label="Расписание"
        />
      </div>
      <div className={styles.datesListMobile}>
        {parsedDates.map((dateEl, index) => (
          <Button
            key={index}
            variant={Variants.Outline}
            onClick={() => handleDateChange(dateEl)}
            text={getLocaleDate(dateEl, locale)}
            palette={Palettes.Secondary}
            className={date === dateEl ? styles.active : styles.date}
          />
        ))}
      </div>
    </div>
  );
};
